/**
 * A tela Contrato do cliente (item 10 da Amanda, protótipo v9 tela 8).
 *
 * Duas leituras, ambas pela sessão (nenhuma recebe id de empresa):
 *
 * · `GET /api/meu-contrato` — datas, escopo com a situação de cada item, dados do cadastro com o
 *   endereço e os metadados do PDF; `GET /api/meu-contrato/arquivo` baixa o PDF.
 * · `GET /api/termos-de-encerramento` — os termos que cada repasse de módulo gera. **Pertence à
 *   frente do repasse** e pode ainda não existir: `404` vira lista vazia ("nenhum módulo repassado
 *   ainda"), e qualquer outra falha aparece como falha, não como vazio.
 *
 * Contrato esperado da rota dos termos (RLS por organização, `contrato:portal`):
 *   `GET  /api/termos-de-encerramento`              → `[{ id, modulo_chave, modulo_nome, nome, tamanho, gerado_em }]`
 *   `GET  /api/termos-de-encerramento/{id}/arquivo` → o PDF (`attachment`).
 */

import { useQuery } from "@tanstack/react-query";
import { z } from "zod";
import { ErroDaApi, pedirJson } from "./api";
import { esquemaContrato } from "./implantacoes";

/** A tela Contrato da matriz de acesso: quem não a tem não lê os dados nem baixa o PDF. */
export const PERMISSAO_DA_TELA_CONTRATO = "contrato:portal";

export const CAMINHO_DO_CONTRATO_DO_CLIENTE = "/app/contrato";
export const CAMINHO_DO_ARQUIVO_DO_CONTRATO = "/api/meu-contrato/arquivo";

export function caminhoDoTermoDeEncerramento(id: string): string {
  return `/api/termos-de-encerramento/${encodeURIComponent(id)}/arquivo`;
}

const esquemaItemContratado = z.object({
  chave: z.string().min(1),
  nome: z.string().min(1),
  modulo_chave: z.string().nullable().default(null),
  situacao: z.string().nullable().default(null),
});

const esquemaEndereco = z.object({
  rotulo: z.string(),
  cep: z.string().nullable(),
  logradouro: z.string().nullable(),
  numero: z.string().nullable(),
  complemento: z.string().nullable(),
  bairro: z.string().nullable(),
  cidade: z.string().nullable(),
  uf: z.string().nullable(),
  principal: z.boolean(),
});

const esquemaPessoa = z.object({
  id: z.string().min(1),
  nome: z.string().min(1),
  email: z.string().min(1),
});

export const esquemaContratoDoCliente = z.object({
  implantacao_id: z.string().min(1),
  contrato: esquemaContrato.nullable(),
  contrato_assinado_em: z.string(),
  prazo_dias: z.number(),
  ambiente_liberado_em: z.string().nullable(),
  kickoff_aceito_em: z.string().nullable(),
  entrega_prevista: z.string().nullable(),
  relogio_iniciado: z.boolean(),
  modulos: z.array(esquemaItemContratado),
  recursos_plus: z.array(esquemaItemContratado),
  empresa: z.object({
    razao_social: z.string().min(1),
    nome_fantasia: z.string().nullable(),
    cnpj: z.string().min(1),
    cidade: z.string().nullable(),
    uf: z.string().nullable(),
    segmento: z.string().nullable(),
    erp: z.string().nullable(),
    ambiente_tipo: z.string(),
    enderecos: z.array(esquemaEndereco),
  }),
  consultor: esquemaPessoa.nullable(),
});

export type ContratoDoCliente = z.infer<typeof esquemaContratoDoCliente>;
export type ItemContratado = z.infer<typeof esquemaItemContratado>;
export type EnderecoDaEmpresa = z.infer<typeof esquemaEndereco>;

export const esquemaTermosDeEncerramento = z.array(
  z.object({
    id: z.string().min(1),
    modulo_chave: z.string().min(1),
    modulo_nome: z.string().min(1),
    nome: z.string().min(1),
    tamanho: z.number(),
    gerado_em: z.string(),
  }),
);

export type TermoDeEncerramento = z.infer<typeof esquemaTermosDeEncerramento>[number];

export const CHAVE_MEU_CONTRATO = ["meu-contrato"] as const;
export const CHAVE_DOS_TERMOS = ["termos-de-encerramento"] as const;

/** `null` = a organização ainda não tem implantação registrada (404 da API), não é falha. */
export function useMeuContrato(habilitada = true) {
  return useQuery<ContratoDoCliente | null>({
    queryKey: CHAVE_MEU_CONTRATO,
    queryFn: async ({ signal }) => {
      try {
        return await pedirJson("/meu-contrato", esquemaContratoDoCliente, { signal });
      } catch (causa) {
        if (causa instanceof ErroDaApi && causa.status === 404) return null;
        throw causa;
      }
    },
    retry: false,
    enabled: habilitada,
  });
}

/** `404` (a rota da outra frente ainda não existe) é lista vazia; o resto é erro de verdade. */
export function useTermosDeEncerramento(habilitada = true) {
  return useQuery<TermoDeEncerramento[]>({
    queryKey: CHAVE_DOS_TERMOS,
    queryFn: async ({ signal }) => {
      try {
        return await pedirJson("/termos-de-encerramento", esquemaTermosDeEncerramento, { signal });
      } catch (causa) {
        if (causa instanceof ErroDaApi && causa.status === 404) return [];
        throw causa;
      }
    },
    retry: false,
    enabled: habilitada,
  });
}

/** O nome do status do Jira como o cliente lê. Sem observação ainda, não há o que afirmar. */
const SITUACAO_DOS_ITENS: Record<string, string> = {
  "to do": "Na fila",
  backlog: "Na fila",
  integration: "Em integração",
  "in progress": "Em andamento",
  training: "Em treinamento",
  "follow up": "Em acompanhamento",
  "waiting customer": "Aguardando você",
  "waiting for customer": "Aguardando você",
  "aguardando cliente": "Aguardando você",
  paused: "Pausado",
  done: "Concluído",
  rejected: "Rejeitado",
};

export type TomDaSituacao = "ok" | "atencao" | "aguardando" | "neutro";

export function situacaoDoItem(situacao: string | null): { rotulo: string; tom: TomDaSituacao } {
  if (!situacao?.trim()) return { rotulo: "Aguardando início", tom: "neutro" };
  const chave = situacao.trim().toLocaleLowerCase("pt-BR");
  const rotulo = SITUACAO_DOS_ITENS[chave] ?? situacao.trim();
  if (rotulo === "Concluído") return { rotulo, tom: "ok" };
  if (rotulo === "Aguardando você") return { rotulo, tom: "atencao" };
  if (rotulo === "Na fila" || rotulo === "Pausado" || rotulo === "Rejeitado") {
    return { rotulo, tom: "neutro" };
  }
  return { rotulo, tom: "aguardando" };
}

function cepFormatado(cep: string | null): string | null {
  if (!cep) return null;
  const digitos = cep.replace(/\D/g, "");
  return digitos.length === 8 ? `${digitos.slice(0, 5)}-${digitos.slice(5)}` : cep;
}

/** Duas linhas: a rua com número e complemento; o bairro, a cidade e o CEP. Vazio some. */
export function linhasDoEndereco(endereco: EnderecoDaEmpresa): string[] {
  const rua = [
    [endereco.logradouro, endereco.numero].filter(Boolean).join(", "),
    endereco.complemento,
  ]
    .filter(Boolean)
    .join(" — ");
  const cidade = [endereco.cidade, endereco.uf].filter(Boolean).join("/");
  const regiao = [endereco.bairro, cidade, cepFormatado(endereco.cep)].filter(Boolean).join(" · ");
  return [rua, regiao].filter(Boolean);
}
